import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatDualCurrency } from '../utils/currency';
import { useCurrency } from '../context/CurrencyContext';
import { getCategoryName } from '../utils/categoryNames';
import { toast } from 'sonner';
import { Heart } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { FeaturedBadge } from '../components/FeaturedBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_FILTER_THRESHOLD = 6;

export const FavoritesPage = () => {
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('__all__');

  useEffect(() => {
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFavorites = async () => {
    try {
      const { data } = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(data);
    } catch (error) {
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (listingId) => {
    try {
      await axios.delete(`${API}/favorites/remove`, {
        data: { listing_id: listingId },
        withCredentials: true
      });
      setFavorites(favorites.filter(f => f.listing_id !== listingId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error(t('favorites.removeFailed'));
    }
  };

  const availableCategories = useMemo(() => {
    const seen = new Map();
    favorites.forEach((l) => {
      if (l.category && !seen.has(l.category)) seen.set(l.category, getCategoryName(l.category, t));
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [favorites, t]);

  const visibleFavorites = useMemo(() => {
    if (categoryFilter === '__all__') return favorites;
    return favorites.filter((l) => l.category === categoryFilter);
  }, [favorites, categoryFilter]);

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-0">
      <Header />

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6 gap-3">
          <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide" data-testid="favorites-title">
            {t('favorites.favorites')}
          </h1>
          {favorites.length > CATEGORY_FILTER_THRESHOLD && availableCategories.length > 1 && (
            <div className="ml-auto shrink-0">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-9 text-sm min-w-[170px] w-auto" data-testid="favorites-category-filter">
                  <SelectValue placeholder={t('profile.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" data-testid="favorites-category-all">
                    {t('profile.allCategories')}
                  </SelectItem>
                  {availableCategories.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`favorites-category-${c.value}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-text_muted">{t('common.loading')}</p>
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12 bg-white border border-border rounded-md" data-testid="no-favorites">
            <Heart size={64} className="text-text_muted mx-auto mb-4" />
            <p className="text-text_muted text-lg mb-2">{t('favorites.noFavorites')}</p>
            <p className="text-sm text-text_muted mb-4">{t('favorites.addFavorites')}</p>
            <Link to="/browse">
              <Button className="bg-primary hover:bg-primary-hover text-white">
                {t('home.browseListings')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="favorites-grid">
            {visibleFavorites.map((listing) => {
              const price = formatDualCurrency(listing.price_mga, secondaryCurrency);
              return (
                <div key={listing.listing_id} className="listing-card bg-white border border-border rounded-xl overflow-hidden relative" data-testid={`favorite-${listing.listing_id}`}>
                  {listing.is_featured && <FeaturedBadge />}
                  
                  <button
                    onClick={() => removeFavorite(listing.listing_id)}
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white z-10"
                    data-testid="remove-favorite-button"
                  >
                    <Heart size={20} weight="fill" className="text-danger" />
                  </button>

                  <Link to={`/listings/${listing.listing_id}`}>
                    <div className="aspect-[4/3] bg-surface">
                      {listing.images && listing.images.length > 0 ? (
                        <img
                          src={`${API}/files/${listing.images[0]}`}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-text_muted">
                          {t('common.noImage')}
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="p-4">
                    <Link to={`/listings/${listing.listing_id}`}>
                      <h3 className="font-semibold font-heading text-sm mb-1 truncate hover:text-primary" data-testid="listing-title">
                        {listing.title}
                      </h3>
                    </Link>
                    <p className="text-text_muted text-xs mb-2">{listing.city} <span className="font-semibold text-primary">• {getCategoryName(listing.category, t)}</span></p>
                    <p className="text-lg font-bold text-primary" data-testid="listing-price">
                      {price.mga}
                    </p>
                    <p className="text-xs text-text_muted">{price.fmg}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};